"use client";

import {JSX, useState} from "react";

import SmartImage from "@/shared/ui/smart-image";

import {faqMoreQuestionsData} from "@/sections/content/faq/faq.constants";
import {MoreQuestions} from "@/sections/content/faq/parts/more-questions/more-questions";
import {type SpaceProps, useSectionSpace} from "@/sections/use-section-space";

import {useTranslation} from "@/shared/i18n/use-translation";
import {ButtonBase} from "@/shared/ui/buttons";
import {HtmlContent} from "@/shared/ui/data-display/html-content";
import {RawEmbedSection} from "@/shared/ui/data-display/raw-embed/raw-embed-iframe";
import {Typography} from "@/shared/ui/data-display/typography";
import {ContainerBase} from "@/shared/ui/layout/container-base";
import {assetPath} from "@/shared/utils/asset-path";

import "@/sections/content/property-management/styles/property-management.scss";

export interface PropertyManagementFaq {
    id: string;
    question: string;
    answer: string;
}

export interface PropertyManagementProps {
    marketName?: string;
    bannerImage?: string;
    bannerImageAlt?: string;
    partnersImage?: string;
    partnersImageAlt?: string;
    leadsImage?: string;
    leadsImageAlt?: string;
    space?: SpaceProps;
    anchorId?: string;
    faqs?: Array<PropertyManagementFaq>;
}

const FORM_CSS = `
		@import url('https://fonts.googleapis.com/css2?family=Poppins:ital,wght@0,100;0,300;0,400;0,500;0,700;0,900;1,100;1,300;1,400;1,500;1,700;1,900&display=swap');

		#leadForm {
				box-sizing: border-box;
				display: block;
				max-width: 450px;
				width: 100%;
				margin-left: auto;
				margin-right: auto;
				padding: 16px 10px 20px;
				background-color: #EFEFEF;
				background-clip: padding-box;
				border: 0px none #CDE0EC;
				border-radius: 5px;
				/* Large radii: clip painting to the curve so parent white (.layout-canvas-g / td) does not show in corners */
				overflow: hidden;
				-webkit-backface-visibility: hidden;
				backface-visibility: hidden;
				box-shadow: 0 0 1px 1px rgba(203, 203, 203, 0.79);
				font-family: 'Poppins', sans-serif;
				color: #6F6F6F;
				font-size: 12px;
				font-weight: 400;
				-webkit-font-smoothing: auto;
		}

		/* Let corner anti-aliasing blend with page/canvas behind the form instead of a white table cell */
		.layout.layout-canvas-g td.camarker-inner:has(#leadForm) {
				background-color: transparent;
		}

		.layout.layout-canvas-g:has(#leadForm) {
				background-color: transparent;
		}

		.layout.layout-canvas-g:has(#leadForm) > .section > .columns {
				background-color: transparent;
		}

		#leadForm *,
		#leadForm *::before,
		#leadForm *::after {
				box-sizing: border-box;
		}

		#leadForm br + br {
				display: none;
		}
#leadForm .name-row {
		display: flex;
		gap: 10px;
}

#leadForm .name-col {
		flex: 1;
		min-width: 0;	 /* evita que los inputs se desborden del contenedor */
}
		#leadForm input[type="text"],
		#leadForm input[type="email"],
		#leadForm input[type="tel"],
		#leadForm input[type="number"],
		#leadForm select,
		#leadForm textarea {
				-webkit-appearance: none;
				-moz-appearance: none;
				appearance: none;
				width: 100%;
				max-width: 100%;
				margin-top: 6px;
				margin-bottom: 16px;
				background-color: #fff !important;
				color: #2C3345 !important;
				border: 0 !important;
				border-radius: 10px !important;
				padding: 8px 15px !important;
				box-shadow: 0 0 5px 1px rgba(203, 203, 203, 0.51);
				font-family: 'Poppins', sans-serif;
				font-size: 14px;
				font-weight: 400;
				line-height: 1.5;
				outline: 0;
				transition: background-color 0.2s ease, box-shadow 0.2s ease;
		}

		#leadForm input[type="text"]:focus,
		#leadForm input[type="email"]:focus,
		#leadForm input[type="tel"]:focus,
		#leadForm input[type="number"]:focus,
		#leadForm select:focus,
		#leadForm textarea:focus {
				background-color: #ECF0F3 !important;
				box-shadow: 0 0 5px 1px rgba(203, 203, 203, 0.65);
		}

		#leadForm textarea {
				min-height: 120px;
				resize: vertical;
		}

		#leadForm select {
				cursor: pointer;
				background-clip: padding-box;
		}

		#leadForm ::placeholder {
				color: #AFB8BC !important;
				font-family: 'Poppins', sans-serif;
				font-size: 14px;
				font-weight: 400;
				opacity: 1;
		}

		#leadForm :-ms-input-placeholder {
				color: #AFB8BC !important;
				font-family: 'Poppins', sans-serif;
				font-size: 14px;
				font-weight: 400;
		}

		#leadForm ::-ms-input-placeholder {
				color: #AFB8BC !important;
				font-family: 'Poppins', sans-serif;
				font-size: 14px;
				font-weight: 400;
		}

		#leadForm input[type="submit"] {
				display: block;
				width: 100%;
				margin-top: 15px;
				margin-bottom: 12px;
				padding: 7px 5px !important;
				background-color: #F57000 !important;
				color: #fff !important;
				border: 0px solid #232323 !important;
				border-radius: 10px !important;
				font-family: 'Poppins', sans-serif;
				font-size: 18px !important;
				font-weight: 500;
				line-height: 1.4;
				cursor: pointer;
				box-shadow: 0 0 0 0 #fff;
				transition: filter 0.2s ease, transform 0.05s ease;
		}

		#leadForm input[type="submit"]:hover {
				filter: brightness(1.05);
		}

		#leadForm input[type="submit"]:active {
				transform: translateY(1px);
		}

		#leadForm small {
				display: block;
				margin-top: 4px;
				color: #6F6F6F;
				font-family: 'Poppins', sans-serif;
				font-size: 11px;
				font-weight: 400;
				line-height: 1.5;
		}

		.sms-consent-group {
			margin: 20px 0;
		}

		.sms-consent-item {
			display: flex;
			align-items: flex-start;
			gap: 10px;
			margin-bottom: 16px;
		}

		.sms-consent-item input[type="checkbox"] {
			margin-top: 3px;
			flex-shrink: 0;
		}

		#thankYouMessage {
				max-width: 450px;
				margin-left: auto;
				margin-right: auto;
				padding: 24px 16px;
				font-family: 'Poppins', sans-serif;
		}

		#thankYouMessage h2 {
				font-family: 'Poppins', sans-serif;
				font-size: 1.25rem;
				font-weight: 600;
				color: #2C3345;
				margin: 0 0 8px;
		}

		#thankYouMessage p {
				margin: 0;
				color: #6F6F6F;
				font-size: 14px;
		}
`;

interface FormLabels {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    zip: string;
    city: string;
    state: string;
    stateDefault: string;
    street: string;
    note: string;
    submit: string;
    thankYouTitle: string;
    thankYouMessage: string;
    disclaimer: string;
}

function buildFormHtml(labels: FormLabels, marketName?: string): string {
    return `
<div class="layout layout-canvas-g">
<div class="section">
				<div class="columns col1">
						<table cellpadding="0" cellspacing="0" width="100%" role="presentation" style="min-width: 100%; " class="stylingblock-content-wrapper">

										<tr>
												<td class="stylingblock-content-wrapper camarker-inner"><form id="leadForm" action="https://webto.salesforce.com/servlet/servlet.WebToLead?encoding=UTF-8&amp;orgId=00D5e000002F1MM" method="POST" target="hidden_iframe" onsubmit="showThankYou()">

<input type="hidden" name="oid" value="00D5e000002F1MM">
<input type="hidden" name="retURL" value="">

<!-- Datos Form -->
<div class="name-row">
		<div class="name-col">First Name* : <input name="first_name" type="text" required=""></div>
		<div class="name-col">Last Name* : <input name="last_name" type="text" required=""></div>
</div>

Email* : <input name="email" type="email" required="">
<br><br>
Phone : <input name="phone" type="tel" pattern="^\+?\d[\d\s\-\(\)]{7,}$">
<br><br>

Zip / Postal Code* : <input name="00N5e00000fWlN5" type="text" inputmode="numeric" pattern="[0-9]{4,10}" placeholder="4070000" required="">
<br><br>
<div class="sms-consent-group">
    <div class="sms-consent-item">

<input id="00NVQ0000088OyD" name="00NVQ0000088OyD" type="checkbox" value="1"> I consent to receive transactional messages from ${marketName} at the phone number provided. Message frequency may vary. Message & Data rates may apply. Reply HELP for help or STOP to opt-out.<br><br>
</div>
   
<div class="sms-consent-item">

<input id="00NVQ0000088P1R" name="00NVQ0000088P1R" type="checkbox" value="1">I consent to receive marketing and promotional messages from ${marketName} at the phone number provided. Message frequency may very. Message & Data rates may apply. Reply HELP for help or STOP to opt-out.
</div>
</div>
<!-- Hidden Fields -->
<input id="utm_campaign" name="00N5e00000fWlMw" type="hidden">
<input id="utm_content" name="00N5e00000fWlMx" type="hidden">
<input id="utm_medium" name="00N5e00000fWlMy" type="hidden">
<input id="utm_source" name="00N5e00000fWlMz" type="hidden">

<input type="hidden" id="lead_source" name="lead_source" value="Web">

<input type="hidden" id="00N5e00000fWlMv" name="00N5e00000fWlMv" value="New">

<input type="hidden" id="url" name="url">

<!-- Submit -->
<input type="submit" value="Submit">

<center><p>
	Have an immediate question?
													</p>

<a target="_blank" rel="noopener noreferrer nofollow" href="https://casago.com/privacy-policy/" title="Privacy Policy">Privacy Policy</a> | <a target="_blank" rel="noopener noreferrer nofollow" href="https://casago.com/sms-terms-and-conditions/" title="Terms of Service">Terms of Service</a>
												</center> </form>

<!-- iframe oculto -->
<iframe name="hidden_iframe" style="display:none;"></iframe>

<!-- mensaje -->
<div id="thankYouMessage" style="display:none;">
<h2>Thanks for submitting this form </h2>
<p>We will contact you soon.</p>
</div>

<script>
function getParam(param) {
	const urlParams = new URLSearchParams(window.location.search);
	return urlParams.get(param);
}


// UTMs
document.getElementById("utm_campaign").value = getParam("utm_campaign") || "";
document.getElementById("utm_content").value = getParam("utm_content") || "";
document.getElementById("utm_medium").value = getParam("utm_medium") || "";
document.getElementById("utm_source").value = getParam("utm_source") || "";

// URL clean
var cleanUrl = window.location.href.split('?')[0];
var field = document.getElementById("url");
if (field) {field.value = cleanUrl;}

// mensaje de éxito
function showThankYou() {
	document.getElementById("leadForm").style.display = "none";
	document.getElementById("thankYouMessage").style.display = "block";
}
</script>
	</td>
										</tr>

						</table>
				</div>
		</div>
</div>
`;
}

const MARKETING_ICONS = ["keyboard-open", "login", "home-hashtag", "check-out"] as const;
const PARTNERS_ICONS = ["verified", "team", "garantee"] as const;

export function PropertyManagement({
    marketName,
    bannerImage,
    bannerImageAlt,
    partnersImage,
    partnersImageAlt,
    leadsImage,
    leadsImageAlt,
    space,
    anchorId,
    faqs,
}: PropertyManagementProps): JSX.Element {
    const sectionSpace = useSectionSpace(space);
    const {t, locale} = useTranslation("propertyManagement");
    const {t: tCommon} = useTranslation("common");
    const [openFaqIds, setOpenFaqIds] = useState<Set<string>>(new Set());
    const [showAllFaqs, setShowAllFaqs] = useState(false);

    const toggleFaq = (id: string): void => {
        setOpenFaqIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) {
                next.delete(id);
            } else {
                next.add(id);
            }
            return next;
        });
    };

    const faqVisibleCount = 5;
    const hasMoreFaqs = (faqs?.length ?? 0) > faqVisibleCount;

    const marketingItems = [
        {
            id: "tech",
            icon: assetPath(`/images/icons/${MARKETING_ICONS[0]}.svg`),
            title: t("marketingItems.tech.title"),
            description: t("marketingItems.tech.description"),
        },
        {
            id: "local",
            icon: assetPath(`/images/icons/${MARKETING_ICONS[1]}.svg`),
            title: t("marketingItems.local.title"),
            description: t("marketingItems.local.description"),
        },
        {
            id: "support",
            icon: assetPath(`/images/icons/${MARKETING_ICONS[2]}.svg`),
            title: t("marketingItems.support.title"),
            description: t("marketingItems.support.description"),
        },
        {
            id: "invest",
            icon: assetPath(`/images/icons/${MARKETING_ICONS[3]}.svg`),
            title: t("marketingItems.invest.title"),
            description: t("marketingItems.invest.description"),
        },
    ];

    const partnersItems = [
        {
            id: "full-svc",
            icon: assetPath(`/images/icons/${PARTNERS_ICONS[0]}.svg`),
            title: t("partnersItems.fullSvc.title"),
            description: t("partnersItems.fullSvc.description"),
        },
        {
            id: "visibility",
            icon: assetPath(`/images/icons/${PARTNERS_ICONS[1]}.svg`),
            title: t("partnersItems.visibility.title"),
            description: t("partnersItems.visibility.description"),
        },
        {
            id: "peace",
            icon: assetPath(`/images/icons/${PARTNERS_ICONS[2]}.svg`),
            title: t("partnersItems.peace.title"),
            description: t("partnersItems.peace.description"),
        },
    ];

    const leadsParagraphs = [t("leadsPara1"), t("leadsPara2"), t("leadsPara3"), t("leadsPara4")];

    const formHtml = buildFormHtml(
        {
            firstName: t("form.firstName"),
            lastName: t("form.lastName"),
            email: t("form.email"),
            phone: t("form.phone"),
            zip: t("form.zip"),
            city: t("form.city"),
            state: t("form.state"),
            stateDefault: t("form.stateDefault"),
            street: t("form.street"),
            note: t("form.note"),
            submit: t("form.submit"),
            thankYouTitle: t("form.thankYouTitle"),
            thankYouMessage: t("form.thankYouMessage"),
            disclaimer: t("form.disclaimer"),
        },
        marketName,
    );

    return (
        <div className="pm" style={sectionSpace} id={anchorId}>
            {/* ── Banner ─────────────────────────────────────────────────── */}
            <div className="pm__banner">
                {bannerImage && (
                    <SmartImage src={bannerImage} alt={bannerImageAlt || ""} fill className="pm__banner-bg" style={{objectFit: "cover"}} />
                )}
                <ContainerBase>
                    <div className="pm__banner-grid">
                        <div className="pm__banner-text">
                            <Typography as="h1" size={{desktop: 40, mobile: 26}} weight={700} className="pm__banner-title">
                                {t("bannerTitle", {marketSuffix: marketName ? ` in ${marketName}` : ""})}
                            </Typography>
                            <Typography as="h2" size={{desktop: 32, mobile: 20}} className="pm__banner-subtitle">
                                {t("bannerSubtitle1")}
                            </Typography>
                            <Typography as="h3" size={{desktop: 24, mobile: 14}} className="pm__banner-subtitle">
                                {t("bannerSubtitle2")}
                            </Typography>
                        </div>
                        <div className="pm__banner-form" id="pm-lead-form">
                            <style dangerouslySetInnerHTML={{__html: FORM_CSS}} />
                            <RawEmbedSection html={formHtml} scopeKey="pm-banner-form" />
                        </div>
                    </div>
                </ContainerBase>
            </div>

            {/* ── Marketing Grid ─────────────────────────────────────────── */}
            <div className="pm__marketing">
                <ContainerBase>
                    <Typography as="h2" size={{desktop: 32, mobile: 22}} weight={700} className="pm__marketing-title">
                        {t("marketingTitle")}
                    </Typography>
                    <div className="pm__marketing-grid">
                        {marketingItems.map((item) => (
                            <div key={item.id} className="pm__marketing-card">
                                <div className="pm__marketing-icon">
                                    <SmartImage src={item.icon} alt={item.title} width={40} height={40} />
                                </div>
                                <Typography as="h4" size={16} weight={600} className="pm__marketing-card-title">
                                    {item.title}
                                </Typography>
                                <Typography as="p" size={14} color="paragraph" className="pm__marketing-card-desc">
                                    {item.description}
                                </Typography>
                            </div>
                        ))}
                    </div>
                </ContainerBase>
            </div>

            {/* ── Reasons to Partner ─────────────────────────────────────── */}
            <div className="pm__partners">
                <ContainerBase>
                    <div className="pm__split pm__split--partners">
                        <div className="pm__content">
                            <Typography as="h2" size={{desktop: 22, mobile: 18}} weight={700} className="pm__block-title">
                                {t("partnersTitle")}
                            </Typography>
                            <ul className="pm__list">
                                {partnersItems.map((item) => (
                                    <li key={item.id} className="pm__list-item">
                                        <div className="pm__list-item-header">
                                            <SmartImage src={item.icon} alt={item.title} width={24} height={24} className="pm__list-item-icon" />
                                            <Typography as="span" size={14} weight={700} className="pm__list-item-title">
                                                {item.title}
                                            </Typography>
                                        </div>
                                        <Typography as="p" size={14} color="paragraph" className="pm__list-item-desc">
                                            {item.description}
                                        </Typography>
                                    </li>
                                ))}
                            </ul>
                            <ButtonBase
                                as="button"
                                variant="primary"
                                size="lg"
                                width="full"
                                className="pm__cta"
                                onClick={() => document.getElementById("pm-lead-form")?.scrollIntoView({behavior: "smooth", block: "center"})}
                            >
                                {t("partnersCtaLabel")}
                            </ButtonBase>
                        </div>
                        {partnersImage && (
                            <div className="pm__media">
                                <div className="pm__img-wrap pm__img-wrap--blue">
                                    <SmartImage src={partnersImage} alt={partnersImageAlt || ""} fill style={{objectFit: "cover"}} />
                                </div>
                            </div>
                        )}
                    </div>
                </ContainerBase>
            </div>

            {/* ── Professional Property Management ───────────────────────── */}
            <div className="pm__leads">
                <ContainerBase>
                    <div className="pm__split pm__split--leads">
                        {leadsImage && (
                            <div className="pm__media">
                                <div className="pm__img-wrap pm__img-wrap--warm">
                                    <SmartImage src={leadsImage} alt={leadsImageAlt || ""} fill style={{objectFit: "cover"}} />
                                </div>
                            </div>
                        )}
                        <div className="pm__content">
                            <Typography as="h2" size={{desktop: 22, mobile: 18}} weight={700} className="pm__block-title">
                                {t("leadsTitle")}
                            </Typography>
                            {leadsParagraphs.map((para, i) => (
                                <Typography key={i} as="p" size={14} color="paragraph" className="pm__para">
                                    {para}
                                </Typography>
                            ))}
                            <ButtonBase
                                as="button"
                                variant="primary"
                                size="lg"
                                width="full"
                                className="pm__cta"
                                onClick={() => document.getElementById("pm-lead-form")?.scrollIntoView({behavior: "smooth", block: "center"})}
                            >
                                {t("leadsCtaLabel")}
                            </ButtonBase>
                        </div>
                    </div>
                </ContainerBase>
            </div>

            {/* ── FAQ ────────────────────────────────────────────────────── */}
            {faqs && faqs.length > 0 && (
                <div className="pm__faq">
                    <ContainerBase>
                        <Typography as="h2" size={{desktop: 32, mobile: 22}} weight={600} className="pm__faq-title">
                            {t("faqsTitle")}
                        </Typography>
                        <div className="pm__faq-wrap">
                            <div className="pm__faq-list">
                                {faqs.map((faq, index) => {
                                    const isOpen = openFaqIds.has(faq.id);
                                    const isHidden = hasMoreFaqs && !showAllFaqs && index >= faqVisibleCount;
                                    return (
                                        <div
                                            key={faq.id}
                                            className={`pm__faq-item ${isOpen ? "pm__faq-item--open" : ""} ${isHidden ? "pm__faq-item--hidden" : "pm__faq-item--visible"}`}
                                        >
                                            <button
                                                type="button"
                                                className="pm__faq-question-wrapper"
                                                onClick={() => toggleFaq(faq.id)}
                                                aria-expanded={isOpen}
                                            >
                                                <Typography as="h3" size={{desktop: 18, mobile: 14}} weight={500} className="pm__faq-question">
                                                    {faq.question}
                                                </Typography>
                                                <span className="pm__faq-icon">
                                                    <span className="pm__faq-icon-line pm__faq-icon-line--horizontal"></span>
                                                    <span className="pm__faq-icon-line pm__faq-icon-line--vertical"></span>
                                                </span>
                                            </button>
                                            <div className="pm__faq-answer-wrapper">
                                                <HtmlContent html={faq.answer} className="pm__faq-answer" />
                                            </div>
                                        </div>
                                    );
                                })}
                                {hasMoreFaqs && !showAllFaqs && (
                                    <ButtonBase
                                        variant="secondary-outline"
                                        size="sm"
                                        width="fit"
                                        className="pm__faq-show-more"
                                        onClick={() => setShowAllFaqs(true)}
                                    >
                                        {tCommon("showMore")}
                                        <SmartImage
                                            src={assetPath("/images/icons/arrow-black-down.svg")}
                                            alt=""
                                            width={12}
                                            height={10}
                                            style={{marginLeft: 8}}
                                        />
                                    </ButtonBase>
                                )}
                            </div>
                            <div className="pm__faq-more-questions">
                                <MoreQuestions {...faqMoreQuestionsData[locale]} />
                            </div>
                        </div>
                    </ContainerBase>
                </div>
            )}
        </div>
    );
}
